import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('accessToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedRefreshToken && storedUser) {
          setAccessToken(storedToken);
          setRefreshToken(storedRefreshToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      return { success: false, token: null };
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken,
      });

      const { accessToken: newAccessToken } = response.data.data;

      setAccessToken(newAccessToken);
      localStorage.setItem('accessToken', newAccessToken);

      return { success: true, token: newAccessToken };
    } catch (err) {
      console.error('Token refresh failed:', err);
      logout();
      return { success: false, token: null };
    }
  }, [refreshToken]);

  // Setup axios interceptor to handle 401 responses
  useEffect(() => {
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshResult = await refreshAccessToken();
          if (refreshResult.success && refreshResult.token) {
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${refreshResult.token}`;
            originalRequest.headers['Authorization'] = `Bearer ${refreshResult.token}`;
            return apiClient(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshAccessToken]);

  // Add token to all requests
  useEffect(() => {
    if (accessToken) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete apiClient.defaults.headers.common['Authorization'];
    }
  }, [accessToken]);

  // Register user
  const register = useCallback(async (email, password, name, role = 'consumer', profileData = {}) => {
    setError(null);
    try {
      const response = await apiClient.post('/auth/register', {
        email,
        password,
        name,
        role,
        // Include optional profile fields if provided
        ...(profileData.phone && { phone: profileData.phone }),
        ...(profileData.location && { location: profileData.location }),
        ...(profileData.country && { country: profileData.country }),
      });

      const { user: userData, accessToken: token, refreshToken: refresh } =
        response.data.data;

      setUser(userData);
      setAccessToken(token);
      setRefreshToken(refresh);
      setIsAuthenticated(true);

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true, user: userData };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Registration failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  // Login user
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password,
      });

      const { user: userData, accessToken: token, refreshToken: refresh } =
        response.data.data;

      setUser(userData);
      setAccessToken(token);
      setRefreshToken(refresh);
      setIsAuthenticated(true);

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true, user: userData };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  // Admin login
  const adminLogin = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await apiClient.post('/auth/admin/login', {
        email,
        password,
      });

      const { user: userData, accessToken: token, refreshToken: refresh } =
        response.data.data;

      setUser(userData);
      setAccessToken(token);
      setRefreshToken(refresh);
      setIsAuthenticated(true);

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true, user: userData };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Admin login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  // Logout user
  const logout = useCallback(async () => {
    try {
      if (accessToken) {
        await apiClient.post('/auth/logout', { refreshToken });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
      setIsAuthenticated(false);
      setError(null);

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      delete apiClient.defaults.headers.common['Authorization'];
    }
  }, [accessToken, refreshToken]);

  // Fetch current user profile
  const fetchProfile = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      const userData = response.data.data;

      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      return userData;
    } catch (err) {
      console.error('Fetch profile error:', err);
      throw err;
    }
  }, []);

  const value = {
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    isAuthenticated,
    register,
    login,
    adminLogin,
    logout,
    fetchProfile,
    refreshAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
